import React, { useRef, useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import CharacterRig from "./CharacterRig";

/**
 * ThirdPersonWalker — third-person locomotion.
 *
 * Inputs (all merged each frame):
 *   - Keyboard (W/S forward, A/D turn, Shift sprint, arrows aliases)
 *   - Joystick / touch via `inputRef.current = { x, y, sprint }`
 *   - Teleport target via `teleportRef.current = THREE.Vector3 | null`
 */
export default function ThirdPersonWalker({
  enabled,
  avatarUrl,
  isRenderableInScene,
  inputRef,
  teleportRef,
}) {
  const { camera } = useThree();
  const keys = useRef({});
  const groupRef = useRef();
  const state = useRef({
    pos: new THREE.Vector3(0, 0, 18),
    facing: Math.PI,
    velocity: new THREE.Vector3(),
    initialised: false,
  });

  useEffect(() => {
    const down = (e) => {
      keys.current[e.code] = true;
    };
    const up = (e) => {
      keys.current[e.code] = false;
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  useEffect(() => {
    if (enabled && !state.current.initialised) {
      state.current.pos.set(0, 0, 18);
      state.current.facing = Math.PI;
      state.current.initialised = true;
    }
    if (!enabled) {
      state.current.initialised = false;
    }
  }, [enabled]);

  useFrame((_, delta) => {
    if (!enabled) return;
    const s = state.current;
    const k = keys.current;
    const touchInput = (inputRef && inputRef.current) || { x: 0, y: 0, sprint: false };

    // Teleport (XR or right-click → click on TeleportTarget)
    if (teleportRef && teleportRef.current) {
      s.pos.copy(teleportRef.current);
      teleportRef.current = null;
    }

    const sprint = k["ShiftLeft"] || touchInput.sprint ? 1.8 : 1;

    // Turn (keyboard A/D OR touch x-axis)
    const turnSpeed = 2.6 * delta;
    let turn = 0;
    if (k["KeyA"] || k["ArrowLeft"]) turn += 1;
    if (k["KeyD"] || k["ArrowRight"]) turn -= 1;
    turn += -(touchInput.x || 0); // joystick x: right turns avatar right (matches keyboard D)
    s.facing += turn * turnSpeed;

    // Forward/back
    const forward = new THREE.Vector3(Math.sin(s.facing), 0, Math.cos(s.facing));
    const moveSpeed = 4 * sprint * delta;
    let fwd = 0;
    if (k["KeyW"] || k["ArrowUp"]) fwd += 1;
    if (k["KeyS"] || k["ArrowDown"]) fwd -= 1;
    fwd += touchInput.y || 0;
    fwd = Math.max(-1, Math.min(1, fwd));

    const dir = forward.clone().multiplyScalar(fwd * moveSpeed);
    if (Math.abs(fwd) > 0.05) {
      s.velocity.lerp(dir, 0.5);
    } else {
      s.velocity.lerp(new THREE.Vector3(), 0.3);
    }
    s.pos.add(s.velocity);

    const limit = 60;
    s.pos.x = THREE.MathUtils.clamp(s.pos.x, -limit, limit);
    s.pos.z = THREE.MathUtils.clamp(s.pos.z, -limit, limit);

    if (groupRef.current) {
      groupRef.current.position.copy(s.pos);
      groupRef.current.rotation.y = s.facing + Math.PI;
    }

    // Camera follow
    const camDist = 5;
    const camHeight = 2.6;
    const camOffset = new THREE.Vector3(
      Math.sin(s.facing + Math.PI) * camDist,
      camHeight,
      Math.cos(s.facing + Math.PI) * camDist
    );
    const targetCam = s.pos.clone().add(camOffset);
    camera.position.lerp(targetCam, Math.min(1, delta * 6));
    const lookAt = s.pos.clone().add(new THREE.Vector3(0, 1.4, 0));
    camera.lookAt(lookAt);
  });

  if (!enabled) return null;

  return (
    <group ref={groupRef}>
      <CharacterRig
        avatarUrl={avatarUrl}
        isRenderableInScene={isRenderableInScene}
      />
    </group>
  );
}
